import { type FC, type ComponentProps, useCallback } from 'react';
import { useFragment, graphql, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { HpSharedItemTilesCarouselModule } from '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarouselModule';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    triggerConstants,
} from 'dibs-tracking';

import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes';
import { type HpSharedHeroCarousel_viewer$key } from './__generated__/HpSharedHeroCarousel_viewer.graphql';
import { type HpSharedHeroCarousel_user$key } from './__generated__/HpSharedHeroCarousel_user.graphql';

const viewerFragment = graphql`
    fragment HpSharedHeroCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedHeroCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        editorsPicks: itemSearchItems(
            first: $count
            after: $cursor
            uriRef: "/collections/editors-picks/"
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) @connection(key: "HpSharedHeroCarousel_editorsPicks", filters: []) {
            edges {
                node {
                    ...HpSharedItemTilesCarouselModule_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarouselModule_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment HpSharedHeroCarousel_user on User {
        ...HpSharedItemTilesCarouselModule_user
    }
`;

const title = (
    <FormattedMessage id="homepage.HpSharedHeroCarousel.title" defaultMessage="Editor's Picks" />
);

const viewMoreLink = '/collections/editors-picks/';

export const HpSharedHeroCarousel: FC<{
    viewer?: HpSharedHeroCarousel_viewer$key | null;
    user?: HpSharedHeroCarousel_user$key | null;
    isMobile: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const onArrowClick: ComponentProps<typeof HpSharedItemTilesCarouselModule>['onArrowClick'] =
        useCallback(() => {
            trackEvent({
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_HOMEPAGE,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_EDITORS_PICKS_ARROW_CLICK,
                trigger: triggerConstants.TRIGGER_EDITORS_PICKS,
            });
        }, []);

    const items = flattenConnectionNodes(viewer?.editorsPicks);
    const totalResults = viewer?.editorsPicks?.totalResults || 0;

    return (
        <HpSharedItemTilesCarouselModule
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            totalResults={totalResults}
            trackingList={triggerConstants.TRIGGER_EDITORS_PICKS}
            loadNext={loadNext}
            onArrowClick={onArrowClick}
            title={title}
            viewMoreLink={viewMoreLink}
            dataTn="editors-picks"
        />
    );
};
